import { logger } from '@/utils/logger';
import {
  signup,
  verifyConfirmCode,
  sendVerificationCode,
} from '@/services/auth/emailAuthService';
import { useAuthStore } from '@/stores/authStore';
import { SubTitle } from '@/styles/Typography';
import { User } from '@/types/app_types';
import React, { useEffect, useRef, useState } from 'react';
import { styled } from 'styled-components';
import { GradientButton } from '../button/Button';
import { Snackbar, useSnackbar } from '../common/Snackbar';
import { Modal } from '../modal/Modal';
import PWChangeModal from './PWChangeModal';
import { CodeInput as SharedCodeInput, CodeInputContainer as SharedCodeInputContainer } from '@/styles/Form';

interface PendingUserData {
  email: string;
  password: string;
  passwordConfirm: string;
  name: string;
}

interface VerificationModalProps {
  isOpen: boolean;
  onClose: () => void;
  email: string;
  pendingSignupData?: PendingUserData;
  onSuccess: () => void;
  mode: 'signup' | 'resetPassword';
}

export const VerificationModal: React.FC<VerificationModalProps> = ({
  isOpen,
  onClose,
  email,
  onSuccess,
  pendingSignupData,
  mode = 'signup',
}) => {
  const [verificationCode, setVerificationCode] = useState(['', '', '', '', '', '']);
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const inputRefs = useRef<(HTMLInputElement | null)[]>([]);
  const { snackbar, showSnackbar, hideSnackbar } = useSnackbar();
  const [isPWChangeModalOpen, setIsPWChangeModalOpen] = useState(false);

  const { setUser } = useAuthStore();

  // 인증 코드 문자열 생성
  const getFullCode = () => verificationCode.join('');

  // 인증 코드 유효성 검사 - 서버 API를 통해 검증
  const handleVerification = async () => {
    if (mode === 'resetPassword' && !email) {
      setError('Email is required for password reset');
      return;
    }

    try {
      setIsLoading(true);
      setError('');

      const response = await verifyConfirmCode(email, getFullCode());

      if (!response.success) {
        throw new Error(response.message || 'Verification failed');
      }

      if (mode === 'signup' && pendingSignupData) {
        const signupData = {
          email: pendingSignupData.email,
          password1: pendingSignupData.password,
          password2: pendingSignupData.passwordConfirm,
          confirm_code: getFullCode(),
          full_name: pendingSignupData.name,
        };

        const signupResponse = await signup(signupData);

        if (!signupResponse?.success) {
          throw new Error(signupResponse?.message || 'Signup failed');
        }

        // zustand 상태 업데이트
        if (signupResponse.data?.user) {
          setUser({
            ...signupResponse.data.user,
            email: email,
            name: signupResponse.data.user.name || '',
          } as User);
        }

        showSnackbar('Verification successful', {
          showCheckIcon: true,
          duration: 3000,
        });

        onSuccess();
        onClose();
      } else if (mode === 'resetPassword') {
        // 비밀번호 재설정 모달 열기
        setIsPWChangeModalOpen(true);
      }
    } catch (err) {
      // 에러 메시지 처리
      const errorMessage = err instanceof Error ? err.message : 'Verification failed';

      if (errorMessage.includes('expired')) {
        setError('Your code has expired. Please request a new one');
      } else if (errorMessage.includes('incorrect')) {
        setError('The code you entered is incorrect');
      } else {
        setError('Verification failed. Please try again later');
      }
    } finally {
      setIsLoading(false);
    }
  };

  // 비밀번호 변경 성공 후 처리
  const handlePWChangeSuccess = () => {
    setIsPWChangeModalOpen(false);
    showSnackbar('Password changed successfully', {
      showCheckIcon: true,
      duration: 3000,
    });
  };

  const handleResendCode = async () => {
    try {
      const response = await sendVerificationCode(email);

      if (!response.success) {
        throw new Error('Failed to resend code');
      }

      // 인증번호 재발송 성공
      setVerificationCode(['', '', '', '', '', '']);
      showSnackbar('Email sent successfully', {
        showCheckIcon: true,
        duration: 3000,
      });
    } catch (err) {
      logger.error('Resend error:', err);
      showSnackbar('Failed to resend email. Please try again.', {
        showCheckIcon: false,
        duration: 3000,
      });
    }
  };

  // 입력값 변경 처리
  const handleInputChange = (index: number, value: string) => {
    // 숫자만 입력 가능
    if (value && !/^\d$/.test(value)) return;

    const newCode = [...verificationCode];
    newCode[index] = value;
    setVerificationCode(newCode);

    // 다음 칸으로 자동 이동
    if (value && index < 5) {
      inputRefs.current[index + 1]?.focus();
    }
  };

  // 백스페이스 처리
  const handleKeyDown = (index: number, e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Backspace' && !verificationCode[index] && index > 0) {
      inputRefs.current[index - 1]?.focus();
    }
  };

  // 붙여넣기 처리
  const handlePaste = (e: React.ClipboardEvent) => {
    e.preventDefault();
    const pastedData = e.clipboardData.getData('text').slice(0, 6);
    const numbers = pastedData.split('').filter(char => /^\d$/.test(char));

    const newCode = [...verificationCode];
    numbers.forEach((num, idx) => {
      if (idx < 6) {
        newCode[idx] = num;
      }
    });

    setVerificationCode(newCode);

    // 마지막 입력된 칸으로 포커스 이동
    const lastFilledIndex = numbers.length - 1;
    if (lastFilledIndex >= 0 && lastFilledIndex < 6) {
      inputRefs.current[Math.min(lastFilledIndex + 1, 5)]?.focus();
    }
  };

  // 모달이 열릴 때 첫 번째 input에 포커스
  useEffect(() => {
    if (isOpen) {
      inputRefs.current[0]?.focus();
    }
  }, [isOpen]);

  // 모달이 닫힐 때 입력값 초기화
  useEffect(() => {
    if (!isOpen) {
      setVerificationCode(['', '', '', '', '', '']);
      setError('');
    }
  }, [isOpen]);

  return (
    <>
      <Modal isOpen={isOpen} onClose={onClose} title=" " size="medium">
        <VerificationContent>
          <Description>
            <h1>{mode === 'signup' ? 'Check your email' : 'Reset Password'}</h1>
            <SubTitle style={{ fontSize: '20px' }}>Enter verification code</SubTitle>
            <SubTitle style={{ fontSize: '20px' }}>{email || 'your@email.com'}</SubTitle>
          </Description>

          <CodeInputContainer>
            {[0, 1, 2, 3, 4, 5].map(index => (
              <CodeInputBox
                key={index}
                ref={el => { inputRefs.current[index] = el; }}
                type="text"
                inputMode="numeric"
                maxLength={1}
                value={verificationCode[index]}
                onChange={e => handleInputChange(index, e.target.value)}
                onKeyDown={e => handleKeyDown(index, e)}
                onPaste={index === 0 ? handlePaste : undefined}
                autoComplete="off"
              />
            ))}
          </CodeInputContainer>

          {error && <ErrorMessage>{error}</ErrorMessage>}

          <ButtonGroup>
            <GradientButton
              onClick={handleVerification}
              loading={isLoading}
              width="100%"
              disabled={getFullCode().length !== 6}
            >
              Continue
            </GradientButton>

            <ResendButton onClick={handleResendCode}>Resend email</ResendButton>

            <BackButton onClick={onClose}>Back</BackButton>
          </ButtonGroup>
        </VerificationContent>
      </Modal>

      <Snackbar
        isOpen={snackbar.isOpen}
        message={snackbar.message}
        onClose={hideSnackbar}
        showCheckIcon={snackbar.showCheckIcon}
        showCloseIcon={snackbar.showCloseIcon}
        duration={snackbar.duration}
      />
      <PWChangeModal
        isOpen={isPWChangeModalOpen}
        onClose={() => setIsPWChangeModalOpen(false)}
        email={email}
        onSuccess={() => {
          handlePWChangeSuccess();
        }}
      />
    </>
  );
};

const VerificationContent = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
`;

const Description = styled.div`
  text-align: center;
  margin-bottom: 48px;
  line-height: normal;

  h1 {
    font-size: 30px;
    font-weight: 700;
    color: ${({ theme }) => theme.colors.fontBlackText};
    margin-bottom: 16px;
  }
`;

const CodeInputContainer = styled(SharedCodeInputContainer)``;

const CodeInputBox = styled(SharedCodeInput)``;

const ErrorMessage = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  color: ${({ theme }) => theme.colors.fontRedError};
  font-size: 14px;
  margin-bottom: 24px;
`;

const ButtonGroup = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 16px;
  margin-top: 48px;
`;

const ResendButton = styled.button`
  background: none;
  border: none;
  color: ${({ theme }) => theme.colors.fontGray[400]};
  font-size: 21px;
  font-weight: 400;
  cursor: pointer;
  margin-top: 24px;

  &:hover {
    opacity: 0.8;
  }
`;

const BackButton = styled.button`
  background: none;
  border: none;
  color: ${({ theme }) => theme.colors.fontGrayLight};
  font-size: 21px;
  font-weight: 400;
  cursor: pointer;
  margin-top: 8px;

  &:hover {
    text-decoration: underline;
  }
`;
